import type { Project, Folder, Thread, ThreadWithMessages, Message, Decision, ProviderConfig, ActivityItem, AuthResponse, User } from '@/types'

const API = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '' : 'http://localhost:3001')

function getToken(): string | null {
  return localStorage.getItem('synapsios_token')
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${API}${path}`, {
    headers,
    ...options,
  })
  if (res.status === 401) {
    localStorage.removeItem('synapsios_token')
    localStorage.removeItem('synapsios_user')
    window.dispatchEvent(new Event('synapsios:logout'))
    throw new Error('Session expired')
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

export const api = {
  // Public (no auth)
  getPublicPlans: () => request<any[]>('/api/public/plans'),

  // Auth
  register: (data: { email: string; password: string; display_name: string }) =>
    request<AuthResponse>('/api/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  login: (data: { email: string; password: string }) =>
    request<AuthResponse>('/api/auth/login', { method: 'POST', body: JSON.stringify(data) }),
  logout: () =>
    request<{ success: boolean }>('/api/auth/logout', { method: 'POST' }),
  getMe: () =>
    request<{ user: User }>('/api/auth/me'),
  updateMe: (data: { display_name?: string; use_own_keys?: boolean; current_password?: string; new_password?: string }) =>
    request<{ user: User }>('/api/auth/me', { method: 'PUT', body: JSON.stringify(data) }),
  getMyKeys: () =>
    request<{ provider_type: string; has_key: boolean; created_at: string }[]>('/api/auth/my-keys'),
  updateMyKey: (providerType: string, data: { api_key: string }) =>
    request<{ success: boolean; has_key: boolean }>(`/api/auth/my-keys/${providerType}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Projects
  getProjects: () => request<Project[]>('/api/projects'),
  getProject: (id: string) => request<Project>(`/api/projects/${id}`),
  createProject: (data: { name: string; description?: string; icon_color?: string; priority?: string; password?: string }) =>
    request<Project>('/api/projects', { method: 'POST', body: JSON.stringify(data) }),
  unlockProject: (id: string, password: string) =>
    request<{ success: boolean }>(`/api/projects/${id}/unlock`, { method: 'POST', body: JSON.stringify({ password }) }),
  updateProject: (id: string, data: Partial<Project>) =>
    request<Project>(`/api/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteProject: (id: string) =>
    request<{ success: boolean }>(`/api/projects/${id}`, { method: 'DELETE' }),

  // Folders
  getFolders: (projectId: string) => request<Folder[]>(`/api/projects/${projectId}/folders`),
  createFolder: (data: { project_id: string; parent_folder_id?: string | null; name: string }) =>
    request<Folder>('/api/folders', { method: 'POST', body: JSON.stringify(data) }),
  updateFolder: (id: string, data: Partial<Folder>) =>
    request<Folder>(`/api/folders/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteFolder: (id: string) =>
    request<{ success: boolean }>(`/api/folders/${id}`, { method: 'DELETE' }),

  // Threads
  getThreads: (projectId: string) => request<Thread[]>(`/api/projects/${projectId}/threads`),
  getThread: (id: string) => request<ThreadWithMessages>(`/api/threads/${id}`),
  createThread: (data: { project_id: string; folder_id?: string | null; title: string; description?: string; default_provider?: string; default_model?: string; language?: string }) =>
    request<Thread>('/api/threads', { method: 'POST', body: JSON.stringify(data) }),
  updateThread: (id: string, data: Partial<Thread>) =>
    request<Thread>(`/api/threads/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteThread: (id: string) =>
    request<{ success: boolean }>(`/api/threads/${id}`, { method: 'DELETE' }),
  archiveThread: (id: string) =>
    request<Thread>(`/api/threads/${id}/archive`, { method: 'POST' }),

  // Import / Export
  exportThread: (id: string, format: string) =>
    request<any>(`/api/threads/${id}/export?format=${format}`),
  exportThreadMarkdown: (id: string) => {
    const token = getToken()
    return `${API}/api/threads/${id}/export?format=markdown${token ? `&token=${encodeURIComponent(token)}` : ''}`
  },
  importThread: (data: { project_id: string; data: any; source_format?: string }) =>
    request<any>('/api/threads/import', { method: 'POST', body: JSON.stringify(data) }),

  // Messages
  sendMessage: (threadId: string, data: { content: string; provider?: string; model?: string }) =>
    request<{ userMessage: Message; assistantMessage: Message | null; error?: string }>(
      `/api/threads/${threadId}/messages`, { method: 'POST', body: JSON.stringify(data) }
    ),

  // Summary
  generateSummary: (threadId: string, data?: { provider?: string; model?: string }) =>
    request<{ summary: string; generated_by: string }>(
      `/api/threads/${threadId}/summary`, { method: 'POST', body: JSON.stringify(data || {}) }
    ),

  // Decisions
  getDecisions: (projectId: string) => request<Decision[]>(`/api/projects/${projectId}/decisions`),
  createDecision: (data: { project_id: string; thread_id?: string; title: string; content: string; type?: string }) =>
    request<Decision>('/api/decisions', { method: 'POST', body: JSON.stringify(data) }),
  deleteDecision: (id: string) =>
    request<{ success: boolean }>(`/api/decisions/${id}`, { method: 'DELETE' }),

  // User API Keys (multi-key)
  getUserKeys: () =>
    request<{ keys: any[]; providers: Record<string, { display_name: string; models: string[] }> }>('/api/user-keys'),
  createUserKey: (data: { provider_type: string; api_key: string; label?: string }) =>
    request<any>('/api/user-keys', { method: 'POST', body: JSON.stringify(data) }),
  updateUserKey: (id: string, data: { api_key?: string; label?: string; is_active?: boolean }) =>
    request<any>(`/api/user-keys/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteUserKey: (id: string) =>
    request<{ success: boolean }>(`/api/user-keys/${id}`, { method: 'DELETE' }),

  // Available models (for current user - own keys + leased + shared)
  getAvailableModels: () =>
    request<{ provider_type: string; display_name: string; models: string[]; sources: string[] }[]>('/api/available-models'),

  // Providers
  getProviders: () => request<ProviderConfig[]>('/api/providers'),
  updateProvider: (providerType: string, data: { api_key?: string; is_active?: boolean; display_name?: string }) =>
    request<ProviderConfig>(`/api/providers/${providerType}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Activity
  getActivity: (limit?: number) => request<ActivityItem[]>(`/api/activity?limit=${limit || 20}`),

  // Wallet
  getWallet: () => request<{ balance: number; total_deposited: number; total_spent: number }>('/api/wallet'),
  getWalletTransactions: (limit?: number, offset?: number) =>
    request<any[]>(`/api/wallet/transactions?limit=${limit || 50}&offset=${offset || 0}`),
  depositWallet: (amount: number) =>
    request<{ balance?: number; transaction_id?: string; checkout_url?: string; session_id?: string }>('/api/wallet/deposit', { method: 'POST', body: JSON.stringify({ amount }) }),
  withdrawWallet: (amount_cents: number) =>
    request<any>('/api/wallet/withdraw', { method: 'POST', body: JSON.stringify({ amount_cents }) }),
  saveInvoiceData: (data: any) =>
    request<any>('/api/wallet/invoice-data', { method: 'PUT', body: JSON.stringify(data) }),

  // Pricing
  getPricing: () => request<any[]>('/api/pricing'),

  // Leasing
  getLeasingListings: (provider?: string, minAvailable?: number) => {
    const params = new URLSearchParams()
    if (provider) params.set('provider', provider)
    if (minAvailable && minAvailable > 0) params.set('min_available', String(minAvailable))
    const qs = params.toString()
    return request<any[]>(`/api/leasing/listings${qs ? `?${qs}` : ''}`)
  },
  getMyListings: () => request<any[]>('/api/leasing/my-listings'),
  createListing: (data: { provider_type: string; model: string; price_per_1k_input: number; price_per_1k_output: number; sharing_mode?: string; usage_limit?: number; api_plan_name?: string; visibility_threshold?: number }) =>
    request<any>('/api/leasing/listings', { method: 'POST', body: JSON.stringify(data) }),
  updateListing: (id: string, data: { price_per_1k_input?: number; price_per_1k_output?: number; is_active?: boolean; discount_percent?: number; promotion_percent?: number; usage_limit?: number; api_plan_name?: string; visibility_threshold?: number }) =>
    request<any>(`/api/leasing/listings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteListing: (id: string) =>
    request<{ success: boolean }>(`/api/leasing/listings/${id}`, { method: 'DELETE' }),

  // API Requests (Looking for API)
  getApiRequests: () => request<any[]>('/api/leasing/requests'),
  getMyApiRequests: () => request<any[]>('/api/leasing/my-requests'),
  createApiRequest: (data: any) =>
    request<any>('/api/leasing/requests', { method: 'POST', body: JSON.stringify(data) }),
  acceptApiRequest: (id: string) =>
    request<any>(`/api/leasing/requests/${id}/accept`, { method: 'PUT' }),
  counterOfferApiRequest: (id: string, data: any) =>
    request<any>(`/api/leasing/requests/${id}/counter-offer`, { method: 'POST', body: JSON.stringify(data) }),
  acceptCounterOffer: (id: string) =>
    request<any>(`/api/leasing/requests/${id}/accept-counter`, { method: 'PUT' }),
  cancelApiRequest: (id: string) =>
    request<any>(`/api/leasing/requests/${id}/cancel`, { method: 'PUT' }),
  deleteApiRequest: (id: string) =>
    request<any>(`/api/leasing/requests/${id}`, { method: 'DELETE' }),

  // AI Agent
  getAgentSessions: () => request<any[]>('/api/agent/sessions'),
  getAgentSession: (id: string) => request<any>(`/api/agent/sessions/${id}`),
  createAgentSession: (data: { topic: string; description?: string; keywords?: string[]; target_count?: number; provider?: string; model?: string }) =>
    request<any>('/api/agent/sessions', { method: 'POST', body: JSON.stringify(data) }),
  generateTitles: (sessionId: string) =>
    request<{ job_id: string }>(`/api/agent/sessions/${sessionId}/generate-titles`, { method: 'POST' }),
  appendTitles: (sessionId: string, count: number) =>
    request<{ job_id: string }>(`/api/agent/sessions/${sessionId}/generate-titles`, { method: 'POST', body: JSON.stringify({ append: true, count }) }),
  generateArticle: (articleId: string) =>
    request<{ job_id: string }>(`/api/agent/articles/${articleId}/generate`, { method: 'POST' }),
  bulkGenerateArticles: (articleIds: string[]) =>
    request<{ job_ids: string[]; count: number }>('/api/agent/articles/bulk-generate', { method: 'POST', body: JSON.stringify({ article_ids: articleIds }) }),
  deleteAgentSession: (id: string) =>
    request<{ success: boolean }>(`/api/agent/sessions/${id}`, { method: 'DELETE' }),
  updateAgentArticle: (id: string, data: { content?: string; title?: string }) =>
    request<any>(`/api/agent/articles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAgentArticle: (id: string) =>
    request<{ success: boolean }>(`/api/agent/articles/${id}`, { method: 'DELETE' }),

  // Background Jobs
  getJob: (id: string) => request<any>(`/api/jobs/${id}`),
  getActiveJobs: () => request<any[]>('/api/jobs'),

  // Publish Queue
  getPublishQueue: () => request<any[]>('/api/publish-queue'),
  addToPublishQueue: (data: { article_id?: string; title: string; content: string; target_site?: string; publish_date?: string; image_url?: string; image_source?: string }) =>
    request<any>('/api/publish-queue', { method: 'POST', body: JSON.stringify(data) }),
  updateQueueItem: (id: string, data: any) =>
    request<any>(`/api/publish-queue/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteQueueItem: (id: string) =>
    request<{ success: boolean }>(`/api/publish-queue/${id}`, { method: 'DELETE' }),
  suggestImage: (query: string) =>
    request<{ images: any[] }>('/api/publish-queue/suggest-image', { method: 'POST', body: JSON.stringify({ query }) }),
  publishQueueItem: (id: string) =>
    request<any>(`/api/publish-queue/${id}/publish`, { method: 'POST' }),
  bulkAddToQueue: (data: { articles: { article_id?: string; title: string; content: string }[]; blog_account_id?: string; frequency?: string; time_mode?: string; time_hour?: string; time_range_start?: string; time_range_end?: string }) =>
    request<{ count: number; items: any[] }>('/api/publish-queue/bulk', { method: 'POST', body: JSON.stringify(data) }),
  reorderQueue: (itemIds: string[]) =>
    request<{ success: boolean }>('/api/publish-queue/reorder', { method: 'PUT', body: JSON.stringify({ item_ids: itemIds }) }),
  getPublishSites: () => request<any[]>('/api/publish-sites'),

  // ContentSEO-AI
  getSeoCampaigns: () => request<any[]>('/api/seo/campaigns'),
  getSeoCampaign: (id: string) => request<any>(`/api/seo/campaigns/${id}`),
  createSeoCampaign: (data: { site_url: string; article_count?: number; provider: string; model: string; blog_account_id?: string; schedule_frequency?: string; schedule_time_mode?: string; schedule_time_value?: string }) =>
    request<any>('/api/seo/campaigns', { method: 'POST', body: JSON.stringify(data) }),
  generateSeoArticle: (id: string) =>
    request<{ job_id: string }>(`/api/seo/articles/${id}/generate`, { method: 'POST' }),
  generateAllSeoArticles: (campaignId: string) =>
    request<{ job_ids: string[]; count: number }>(`/api/seo/campaigns/${campaignId}/generate-all`, { method: 'POST' }),
  publishAllSeoArticles: (campaignId: string) =>
    request<{ count: number }>(`/api/seo/campaigns/${campaignId}/publish-all`, { method: 'POST' }),
  deleteSeoCampaign: (id: string) =>
    request<{ success: boolean }>(`/api/seo/campaigns/${id}`, { method: 'DELETE' }),

  // App Builder
  getAppProjects: () => request<any[]>('/api/app-projects'),
  getAppProject: (id: string) => request<any>(`/api/app-projects/${id}`),
  createAppProject: (data: { name: string; description?: string; prompt?: string }) =>
    request<any>('/api/app-projects', { method: 'POST', body: JSON.stringify(data) }),
  updateAppProject: (id: string, data: { name?: string; description?: string; status?: string }) =>
    request<any>(`/api/app-projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAppProject: (id: string) =>
    request<{ success: boolean }>(`/api/app-projects/${id}`, { method: 'DELETE' }),
  saveAppFile: (projectId: string, data: { path: string; content: string; language?: string; is_directory?: boolean }) =>
    request<any>(`/api/app-projects/${projectId}/files`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteAppFile: (projectId: string, fileId: string) =>
    request<{ success: boolean }>(`/api/app-projects/${projectId}/files/${fileId}`, { method: 'DELETE' }),
  generateAppCode: (projectId: string, data: { prompt: string; provider?: string; model?: string }) =>
    request<{ summary: string; files: any[]; errors: any[]; tokensUsed: number }>(
      `/api/app-projects/${projectId}/generate`, { method: 'POST', body: JSON.stringify(data) }
    ),
  getAppErrors: (projectId: string) => request<any[]>(`/api/app-projects/${projectId}/errors`),
  resolveAppError: (projectId: string, errorId: string) =>
    request<{ success: boolean }>(`/api/app-projects/${projectId}/errors/${errorId}/resolve`, { method: 'PUT' }),
  clearAppErrors: (projectId: string) =>
    request<{ success: boolean }>(`/api/app-projects/${projectId}/errors`, { method: 'DELETE' }),
  getAppPreviewUrl: (projectId: string) => {
    const token = getToken()
    return `${API}/api/app-projects/${projectId}/preview${token ? `?token=${token}` : ''}`
  },
  getAppExportUrl: (projectId: string) => {
    const token = getToken()
    return `${API}/api/app-projects/${projectId}/export${token ? `?token=${token}` : ''}`
  },

  // Agent SocialAI - Accounts
  getSocialAccounts: () => request<any[]>('/api/social/accounts'),
  connectSocialAccount: (data: { platform: string; account_name: string; login?: string; password?: string; access_token?: string; profile_url?: string }) =>
    request<any>('/api/social/accounts', { method: 'POST', body: JSON.stringify(data) }),
  updateSocialAccount: (id: string, data: any) =>
    request<any>(`/api/social/accounts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  testSocialAccount: (id: string) =>
    request<{ success: boolean; status: string }>(`/api/social/accounts/${id}/test`, { method: 'POST' }),
  disconnectSocialAccount: (id: string) =>
    request<{ success: boolean }>(`/api/social/accounts/${id}`, { method: 'DELETE' }),

  // Agent SocialAI - Projects (account groups)
  getSocialProjects: () => request<any[]>('/api/social/projects'),
  getSocialProject: (id: string) => request<any>(`/api/social/projects/${id}`),
  createSocialProject: (data: { name: string; description?: string; icon_color?: string; account_ids?: string[] }) =>
    request<any>('/api/social/projects', { method: 'POST', body: JSON.stringify(data) }),
  updateSocialProject: (id: string, data: any) =>
    request<any>(`/api/social/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSocialProject: (id: string) =>
    request<{ success: boolean }>(`/api/social/projects/${id}`, { method: 'DELETE' }),

  // Agent SocialAI - Campaigns
  getSocialCampaigns: (socialProjectId?: string) =>
    request<any[]>(`/api/social/campaigns${socialProjectId ? `?social_project_id=${socialProjectId}` : ''}`),
  getSocialCampaign: (id: string) => request<any>(`/api/social/campaigns/${id}`),
  createSocialCampaign: (data: {
    name?: string; topic: string; description?: string; keywords?: string[];
    info_sources?: string[]; target_count?: number; goal?: string;
    platforms?: string[]; schedule_frequency?: string;
    schedule_time_mode?: string; schedule_fixed_hour?: string;
    schedule_range_start?: string; schedule_range_end?: string;
    provider?: string; model?: string; social_project_id?: string
  }) => request<any>('/api/social/campaigns', { method: 'POST', body: JSON.stringify(data) }),
  updateSocialCampaign: (id: string, data: any) =>
    request<any>(`/api/social/campaigns/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSocialCampaign: (id: string) =>
    request<{ success: boolean }>(`/api/social/campaigns/${id}`, { method: 'DELETE' }),
  generateSocialPosts: (campaignId: string) =>
    request<{ posts: any[] }>(`/api/social/campaigns/${campaignId}/generate`, { method: 'POST' }),

  // Agent SocialAI - Posts
  approveSocialPost: (postId: string) =>
    request<any>(`/api/social/posts/${postId}/approve`, { method: 'POST' }),
  rejectSocialPost: (postId: string, reason?: string) =>
    request<any>(`/api/social/posts/${postId}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),
  updateSocialPost: (postId: string, data: { content?: string; hashtags?: string[]; image_url?: string; platform?: string }) =>
    request<any>(`/api/social/posts/${postId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSocialPost: (postId: string) =>
    request<{ success: boolean }>(`/api/social/posts/${postId}`, { method: 'DELETE' }),

  // Admin
  adminCheck: () => request<{ is_super_admin: boolean }>('/api/admin/check'),
  adminGetStats: () => request<any>('/api/admin/stats'),
  adminGetUsers: (search?: string, limit?: number, offset?: number) =>
    request<{ users: any[]; total: number }>(`/api/admin/users?limit=${limit || 50}&offset=${offset || 0}${search ? `&search=${encodeURIComponent(search)}` : ''}`),
  adminGetUserKeys: (userId: string) => request<any[]>(`/api/admin/users/${userId}/keys`),
  adminUpdateUser: (userId: string, data: any) =>
    request<any>(`/api/admin/users/${userId}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeleteUser: (userId: string) =>
    request<{ success: boolean }>(`/api/admin/users/${userId}`, { method: 'DELETE' }),
  adminGetCommission: () => request<{ commission_percent: number; commission_fixed_cents: number }>('/api/admin/commission'),
  adminUpdateCommission: (data: { commission_percent?: number; commission_fixed_cents?: number }) =>
    request<any>('/api/admin/commission', { method: 'PUT', body: JSON.stringify(data) }),
  adminGetPromoCodes: () => request<any[]>('/api/admin/promo-codes'),
  adminCreatePromoCode: (data: any) =>
    request<any>('/api/admin/promo-codes', { method: 'POST', body: JSON.stringify(data) }),
  adminUpdatePromoCode: (id: string, data: any) =>
    request<any>(`/api/admin/promo-codes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeletePromoCode: (id: string) =>
    request<{ success: boolean }>(`/api/admin/promo-codes/${id}`, { method: 'DELETE' }),
  adminGetPlans: () => request<any[]>('/api/admin/plans'),
  adminCreatePlan: (data: any) =>
    request<any>('/api/admin/plans', { method: 'POST', body: JSON.stringify(data) }),
  adminUpdatePlan: (id: string, data: any) =>
    request<any>(`/api/admin/plans/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  adminDeletePlan: (id: string) =>
    request<{ success: boolean }>(`/api/admin/plans/${id}`, { method: 'DELETE' }),
  adminGetListings: () => request<any[]>('/api/admin/listings'),
  adminDeleteListing: (id: string) =>
    request<{ success: boolean }>(`/api/admin/listings/${id}`, { method: 'DELETE' }),

  // Search
  search: (q: string) => request<{ projects: any[]; items: any[] }>(`/api/search?q=${encodeURIComponent(q)}`),
  getProjectFiles: (projectId: string) => request<import('@/types').UserFile[]>(`/api/projects/${projectId}/files`),

  // File manager
  getFiles: () => request<import('@/types').UserFile[]>('/api/files'),
  getFileContent: (id: string) => request<import('@/types').UserFile>(`/api/files/${id}/content`),
  uploadFiles: (formData: FormData) => {
    const token = getToken()
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    return fetch(`${API}/api/files/upload`, { method: 'POST', headers, body: formData })
      .then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({ error: r.statusText }))
          throw new Error(err.error || `HTTP ${r.status}`)
        }
        return r.json() as Promise<import('@/types').UserFile[]>
      })
  },
  updateFile: (id: string, data: { content?: string; name?: string; folder_id?: string | null }) =>
    request<import('@/types').UserFile>(`/api/files/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteFile: (id: string) =>
    request<{ success: boolean }>(`/api/files/${id}`, { method: 'DELETE' }),
  bulkDeleteFiles: (ids: string[]) =>
    request<{ success: boolean; deleted: number }>('/api/files/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }),
  getProjectFilesDownloadUrl: (projectId: string, folderId?: string) => {
    const token = getToken()
    const qs = new URLSearchParams()
    if (folderId) qs.set('folder_id', folderId)
    if (token) qs.set('token', token)
    return `${API}/api/projects/${projectId}/files/download${qs.toString() ? `?${qs}` : ''}`
  },
}
